import { capitalize, upperFirst } from "lodash";
import {
  CompetencyAppliesTo,
  CompetencyCriteriaUniqueness,
  OrganizationCompetencyFragment,
} from "types/graphql-schema";

import { competencyCriteriaUniquenessLabels } from "@apps/org-settings/constants";
import { UseLabelType } from "@apps/use-label/use-label";
import { assertEdgesNonNull } from "@helpers/helpers";

// How far a competency reaches, which is also how much damage an edit here
// can do: "all" everywhere, "some" beyond this career track, "local" not.
export type CompetencySharingTone = "all" | "some" | "local";

export type CompetencySharing = {
  tone: CompetencySharingTone;
  // One line saying what an edit here reaches (career track table).
  effect: string;
  // The same reach, for deleting a single criterion.
  deleteEffect: string;
  // Who has it, on its own: departments, career tracks, role types.
  reach: string;
  // The full picture in one line: who has it, and how criteria are written
  // (the competencies list).
  summary: string;
  // Everything, untruncated, for the chip's tooltip.
  tooltip: string;
};

// "Engineering", "Engineering and Design", "Engineering, Design and 3 others".
export const formatNameList = (names: string[], maxNamed = 3) => {
  if (names.length <= maxNamed) {
    return names.length > 1
      ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
      : (names[0] ?? "");
  }
  const remaining = names.length - maxNamed;
  return `${names.slice(0, maxNamed).join(", ")} and ${remaining} other${
    remaining === 1 ? "" : "s"
  }`;
};

// The tag under a competency's name in the tables: who it applies to, in a
// few words. "Applies to Engineering", "Applies to this career track",
// "Applies to ICs in Engineering". The track being viewed is "this career
// track", and named first.
export const getCompetencyAppliesToTag = (
  competency: Pick<
    OrganizationCompetencyFragment,
    | "appliesTo"
    | "appliesToIcRoles"
    | "appliesToManagementRoles"
    | "appliesToTeams"
    | "appliesToCareerTracks"
  >,
  careerTrackId?: number | null,
) => {
  if (competency.appliesTo === CompetencyAppliesTo.AllRoles) {
    return "Applies to every role";
  }
  // One role type narrows it; both is the same as neither.
  const roleType =
    competency.appliesToIcRoles && !competency.appliesToManagementRoles
      ? "ICs"
      : competency.appliesToManagementRoles && !competency.appliesToIcRoles
        ? "managers"
        : null;
  const teams = assertEdgesNonNull(competency.appliesToTeams);
  const careerTracks = assertEdgesNonNull(competency.appliesToCareerTracks);
  if (teams.length === 0 && careerTracks.length === 0) {
    if (roleType) {
      return `Applies to all ${roleType}`;
    }
    return competency.appliesToIcRoles && competency.appliesToManagementRoles
      ? "Applies to every role"
      : "Not used in any career track yet";
  }
  const isThisTrack = (track: { id: number }) =>
    !!careerTrackId && track.id === careerTrackId;
  const names = [
    ...(careerTracks.some(isThisTrack) ? ["this career track"] : []),
    ...teams.map((team) => team.title),
    ...careerTracks
      .filter((track) => !isThisTrack(track))
      .map((track) => track.title),
  ];
  return roleType
    ? `Applies to ${roleType} in ${formatNameList(names)}`
    : `Applies to ${formatNameList(names)}`;
};

export const getCompetencySharing = (
  competency: Pick<
    OrganizationCompetencyFragment,
    | "appliesTo"
    | "appliesToIcRoles"
    | "appliesToManagementRoles"
    | "appliesToTeams"
    | "appliesToCareerTracks"
    | "criteriaUniqueness"
  >,
  {
    label,
    careerTrackId,
    context = "track",
  }: {
    label: UseLabelType;
    // The career track being viewed, so its own name isn't counted as sharing.
    careerTrackId?: number | null;
    // "track" while editing a career track, "list" for the competencies list.
    context?: "track" | "list";
  },
): CompetencySharing => {
  const teams = assertEdgesNonNull(competency.appliesToTeams);
  const careerTracks = assertEdgesNonNull(competency.appliesToCareerTracks);
  const otherCareerTracks = careerTracks.filter(
    (track) => track.id !== careerTrackId,
  );
  const appliesToAllRoles =
    competency.appliesTo === CompetencyAppliesTo.AllRoles;
  const isSameAcrossRoles =
    competency.criteriaUniqueness ===
    CompetencyCriteriaUniqueness.SameAcrossRoles;
  // On a career track the reader is about to type, so say what that changes;
  // in the list there's nothing to edit, so describe the reach.
  const isEditing = context === "track";
  const departmentsLabel = label("team", { capitalize: true, pluralize: true });

  const roleTypes = [
    competency.appliesToIcRoles ? "individual contributor" : null,
    competency.appliesToManagementRoles ? "management" : null,
  ].filter(Boolean) as string[];

  const hasScope = teams.length > 0 || careerTracks.length > 0;
  // A role type with departments or career tracks means only roles of that
  // type in them, not every one. Both role types is the same as neither.
  const scopedRoleType =
    roleTypes.length === 1 && hasScope ? roleTypes[0] : null;
  const scopeNames = formatNameList([
    ...teams.map((team) => team.title),
    ...careerTracks.map((track) => track.title),
  ]);

  // Only one career track and nothing else, which the track table files with
  // that track's own (getCompetencyScopeSection): editing it reaches no one
  // else, whatever its role type.
  const isOnOneTrackOnly =
    teams.length === 0 &&
    careerTracks.length === 1 &&
    (!careerTrackId || careerTracks[0].id === careerTrackId);

  const selectedScope = `selected ${[
    teams.length > 0 ? label("team", { pluralize: true }) : null,
    careerTracks.length > 0 ? "career tracks" : null,
  ]
    .filter(Boolean)
    .join(" and ")}`;
  const appliesToValue = appliesToAllRoles
    ? "All roles"
    : scopedRoleType
      ? `${capitalize(scopedRoleType)} roles in ${selectedScope}`
      : roleTypes.length > 0 && !hasScope
        ? `All ${roleTypes.join(" and ")} roles`
        : hasScope
          ? upperFirst(selectedScope)
          : "Career tracks you add it to";

  // Exhaustive one-liner for the list: every department and career track it
  // reaches (or "all roles"), plus how criteria are written.
  const reachSummary = appliesToAllRoles
    ? "All roles"
    : [
        roleTypes.length > 0 && !hasScope
          ? `All ${roleTypes.join(" and ")} roles`
          : null,
        teams.length > 0
          ? `${formatNameList(teams.map((team) => team.title))} ${label(
              "team",
              {
                pluralize: teams.length > 1,
              },
            )}`
          : null,
        careerTracks.length > 0
          ? `${formatNameList(
              careerTracks.map((track) => track.title),
            )} career track${careerTracks.length > 1 ? "s" : ""}`
          : null,
      ]
        .filter(Boolean)
        .join(", ") || "Not used in any career track yet";
  const reach = scopedRoleType
    ? `${capitalize(scopedRoleType)} roles in ${reachSummary}`
    : reachSummary;

  const summary = `${reach} · Criteria ${
    isSameAcrossRoles ? "shared across roles" : "unique per role"
  }`;

  const { tone, effect, deleteEffect } = ((): {
    tone: CompetencySharingTone;
    effect: string;
    deleteEffect: string;
  } => {
    // Per-role criteria are local however widely the competency applies.
    if (!isSameAcrossRoles) {
      return {
        tone: "local",
        effect: isEditing
          ? "Editing here only affects this role."
          : "Criteria are written per role.",
        deleteEffect: "It's only used by this role.",
      };
    }
    if (appliesToAllRoles) {
      return {
        tone: "all",
        effect: isEditing
          ? `Editing here affects every role in your ${label("organization")}.`
          : `Shared with every role in your ${label("organization")}.`,
        deleteEffect: `It's removed from every role in your ${label(
          "organization",
        )}.`,
      };
    }
    if (scopedRoleType && !isOnOneTrackOnly) {
      return {
        tone: "some",
        effect: `${
          isEditing ? "Editing here affects" : "Shared with"
        } every ${scopedRoleType} role in ${scopeNames}.`,
        deleteEffect: `It's removed from every ${scopedRoleType} role in ${scopeNames}.`,
      };
    }
    if (roleTypes.length === 2 && !hasScope) {
      return {
        tone: "all",
        effect: `${
          isEditing ? "Editing here affects" : "Shared with"
        } every individual contributor and management role.`,
        deleteEffect:
          "It's removed from every individual contributor and management role.",
      };
    }
    if (roleTypes.length === 1 && !hasScope) {
      return {
        tone: "some",
        effect: `${
          isEditing ? "Editing here affects" : "Shared with"
        } every ${roleTypes[0]} role.`,
        deleteEffect: `It's removed from every ${roleTypes[0]} role.`,
      };
    }
    // Departments and other tracks at once: name both, or an edit here could
    // reach a whole department without saying so.
    if (teams.length > 0 && otherCareerTracks.length > 0) {
      const names = `every role in ${formatNameList(
        teams.map((team) => team.title),
      )}, and the ${formatNameList(
        otherCareerTracks.map((track) => track.title),
      )} career track${otherCareerTracks.length > 1 ? "s" : ""}`;
      return {
        tone: "some",
        effect: isEditing
          ? `Editing here also affects ${names}.`
          : `Shared with ${names}.`,
        deleteEffect: `It's removed from ${names}.`,
      };
    }
    if (otherCareerTracks.length > 0 && !isOnOneTrackOnly) {
      const names = formatNameList(
        otherCareerTracks.map((track) => track.title),
      );
      return {
        tone: "some",
        effect: isEditing
          ? `Editing here also affects ${names}.`
          : `Shared with ${names}.`,
        deleteEffect: `It's removed from ${names} too.`,
      };
    }
    if (teams.length > 0) {
      const names = formatNameList(teams.map((team) => team.title));
      return {
        tone: "some",
        effect: isEditing
          ? `Editing here also affects every role in ${names}.`
          : `Shared with every role in ${names}.`,
        deleteEffect: `It's removed from every role in ${names}.`,
      };
    }
    // Shared criteria, but nothing outside one career track has it.
    return {
      tone: "local",
      effect: isEditing
        ? "Editing here only affects this career track."
        : careerTracks.length > 0
          ? `Only used in ${formatNameList(
              careerTracks.map((track) => track.title),
            )}.`
          : "Not used in any career track yet.",
      deleteEffect: "It's only used by this career track.",
    };
  })();

  // Untruncated, so a shortened list never hides anything.
  const tooltip = [
    `Applies to: ${appliesToValue}`,
    teams.length > 0
      ? `${departmentsLabel}: ${teams.map((team) => team.title).join(", ")}`
      : null,
    careerTracks.length > 0
      ? `Career tracks: ${careerTracks.map((track) => track.title).join(", ")}`
      : null,
    competencyCriteriaUniquenessLabels[competency.criteriaUniqueness],
    effect,
  ]
    .filter(Boolean)
    .join("\n");

  return { tone, effect, deleteEffect, reach, summary, tooltip };
};
